/* Optionales Backend (PocketBase + Asset-Server): Verbindung, Anmeldung, Assets, Karten und Einstellungen.
 * Ohne DOM; fetch wird injiziert, damit sich alles mit node prüfen lässt. Der Editor funktioniert ohne Backend unverändert. */
import type { Snapshot } from './settings.ts';
import { isSoundFile, soundUploadPath } from './sounds.ts';

const STORE = 'xf-backend-v1';

const TYPE_DIRS: Record<string, string> = { wad: 'wads', spr: 'sprites', tga: 'skybox', bmp: 'skybox', wav: 'sounds', mp3: 'sounds', ogg: 'sounds', mdl: 'models', map: 'maps' };

export interface BackendUser { id: string; email: string; role: 'user' | 'admin' }
export interface HealthInfo { name: string; version: string; capabilities?: Record<string, boolean>; signup?: boolean }
export interface AssetInfo { id: string; path: string; kind: string; size: number; sha256: string; owner: string; mtime: string }
/** Eigene Rolle an einer Karte: Besitzer, Admin, schreibend oder lesend freigegeben */
export type MapAccess = 'owner' | 'admin' | 'write' | 'read';
export interface MapMeta { id: string; name: string; rev: number; size: number; owner: string; updated: string; access?: MapAccess; ownerEmail?: string }
export interface ShareInfo { user: string; email: string; perm: 'read' | 'write' }
export interface MapFull extends MapMeta { text: string }
export interface RevisionList { current: number; items: { rev: number; size: number; created: string }[] }
export interface CompileStep { name: string; state: string; durMs: number; exit: number; detail?: string }
export interface CompileJob {
  id: string; map: string; mapName: string; mapRev: number; profile: string; engine?: string; threads?: number;
  state: 'queued' | 'running' | 'ok' | 'failed' | 'cancelled' | 'timeout' | 'interrupted';
  step: string; reason: string; message: string; steps: CompileStep[] | null; started: number; ended: number; created: string;
  artifacts: { name: string; size: number }[] | null; logBytes: number; owner: string;
  /** nur für Admins in „alle Jobs“-Listen */
  ownerEmail?: string;
}
/** Aktiver Job in der Warteschlange: position 0 = läuft, sonst Platz unter allen wartenden Jobs */
export interface QueueItem extends CompileJob { position: number }
export interface QueueInfo { slots: number; running: number; queued: number; items: QueueItem[] }
export interface StoredSettings { base: string; token: string; email: string; user: BackendUser | null }

/** Fehler einer Server-Antwort; data trägt Zusatzangaben, z. B. { rev } bei einem Speicherkonflikt (409) */
export interface ApiError extends Error { status: number; data: Record<string, unknown> | null }

type FetchFn = (url: string, init: { method: string; headers: Record<string, string>; body?: BodyInit | null }) => Promise<Response>;
export interface ClientOptions {
  base?: string;
  fetch?: FetchFn;
  token?: string;
  user?: BackendUser | null;
  onAuthLost?: (err: ApiError) => void;
}
interface CallInit { headers?: Record<string, string>; body?: BodyInit | null; noAuth?: boolean }

/** Server-URL ohne Slash am Ende; leer = gleiche Origin wie die Seite */
const normBase = (u?: string): string => String(u || '').trim().replace(/\/+$/, '');

/** Zielpfad im Asset-Namensraum für einen Dateinamen (klein geschrieben, Ordner nach Dateityp).
 * Sounds behalten ihren Ordner unterhalb von sound/, wenn rel (relativer Pfad beim Ordner-Upload) angegeben ist. */
function targetPath(filename: string, rel?: string): string {
  if (isSoundFile(filename)) return soundUploadPath(filename, rel);
  const name = String(filename || '').replace(/^.*[\\/]/, '').trim().toLowerCase();
  const ext = (name.match(/\.([a-z0-9]+)$/) || [])[1];
  return (TYPE_DIRS[ext] || 'other') + '/' + name;
}

/** Dateinamen der WADs aus dem wad-Keyvalue einer Map: "\valve\halflife.wad;\valve\sdhlt.wad" → ["halflife.wad", "sdhlt.wad"] */
function wadNames(kv: unknown): string[] {
  const out: string[] = [];
  String(kv || '').split(';').forEach((p) => {
    const n = p.trim().replace(/^.*[\\/]/, '').toLowerCase();
    if (/\.wad$/.test(n) && out.indexOf(n) < 0) out.push(n);
  });
  return out;
}

const encPath = (p: string): string => String(p).split('/').map(encodeURIComponent).join('/');

function fail(res: { status: number }, text: string): ApiError {
  let msg = '', data: Record<string, unknown> | null = null;
  try { const j = JSON.parse(text); msg = j.message || ''; data = j.data || null; } catch (e) { msg = String(text || '').slice(0, 200); }
  const err = new Error(msg || ('HTTP ' + res.status)) as ApiError;
  err.status = res.status;
  err.data = data;
  return err;
}

function create(opts?: ClientOptions) {
  const o = opts || {};
  const base = normBase(o.base);
  const doFetch: FetchFn | null = o.fetch || (typeof fetch !== 'undefined' ? (fetch.bind(globalThis) as unknown as FetchFn) : null);
  let token = o.token || '';
  let user: BackendUser | null = o.user || null;   // { id, email, role } des angemeldeten Benutzers
  const onAuthLost = o.onAuthLost || function () {};
  if (!doFetch) throw new Error('kein fetch verfügbar');
  const send = doFetch;

  async function call(method: string, path: string, init?: CallInit): Promise<Response> {
    const i = init || {};
    const headers = Object.assign({}, i.headers || {});
    if (token && !i.noAuth) headers.Authorization = token;
    const res = await send(base + path, { method: method, headers: headers, body: i.body });
    if (!res.ok) {
      const err = fail(res, await res.text().catch(() => ''));
      // Abgelaufenes oder ungültiges Token: Anmeldung verwerfen und melden (nicht beim Login selbst)
      if (res.status === 401 && token && !i.noAuth) { token = ''; user = null; onAuthLost(err); }
      throw err;
    }
    return res;
  }
  async function jsonCall<T>(method: string, path: string, obj: unknown): Promise<T> {
    return (await call(method, path, { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(obj) })).json() as Promise<T>;
  }

  return {
    base: base,
    get token(): string { return token; },
    get user(): BackendUser | null { return user; },
    setToken(t?: string): void { token = t || ''; if (!token) user = null; },
    url: (p: string): string => base + '/assets/' + encPath(p),
    // Verbindungstest. Liefert { name, version, capabilities } oder wirft.
    async health(): Promise<HealthInfo> {
      const j = await (await call('GET', '/api/xf/health', { noAuth: true })).json();
      if (!j || j.name !== 'xen-forge') throw new Error('kein Xen-Forge-Backend');
      return j as HealthInfo;
    },
    // Anmeldung als Benutzer der Collection „users“. Merkt Token und Benutzer im Client und gibt das Token zurück.
    async login(email: string, password: string): Promise<string> {
      const res = await call('POST', '/api/collections/users/auth-with-password', {
        noAuth: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ identity: email, password: password })
      });
      const j = await res.json();
      if (!j.token) throw new Error('keine Antwort mit Token');
      token = j.token;
      user = { id: String((j.record && j.record.id) || ''), email: String((j.record && j.record.email) || email), role: (j.record && j.record.role) || 'user' };
      return token;
    },
    // Token verlängern (auth-refresh) und Benutzer neu lesen; wirft bei abgelaufenem Token (dann ist eine neue Anmeldung nötig)
    async refresh(): Promise<BackendUser> {
      const j = await (await call('POST', '/api/collections/users/auth-refresh')).json();
      if (!j.token) throw new Error('keine Antwort mit Token');
      token = j.token;
      user = { id: String((j.record && j.record.id) || ''), email: String((j.record && j.record.email) || ''), role: (j.record && j.record.role) || 'user' };
      return user;
    },
    // Alle Assets (paginiert abgeholt), optional gefiltert
    async list(prefix?: string, kind?: string): Promise<AssetInfo[]> {
      const items: AssetInfo[] = []; let off = 0, total = 0;
      do {
        const q = '?limit=500&offset=' + off + (prefix ? '&prefix=' + encodeURIComponent(prefix) : '') + (kind ? '&kind=' + encodeURIComponent(kind) : '');
        const j = await (await call('GET', '/api/xf/assets' + q)).json();
        total = j.total || 0;
        if (!j.items || !j.items.length) break;
        items.push(...(j.items as AssetInfo[])); off += j.items.length;
      } while (off < total);
      return items;
    },
    async upload(path: string, body: BodyInit): Promise<AssetInfo> { return (await call('PUT', '/api/xf/assets/' + encPath(path), { body: body })).json() as Promise<AssetInfo>; },
    async remove(path: string): Promise<void> { await call('DELETE', '/api/xf/assets/' + encPath(path)); },
    // ---- Karten (maps): Speichern mit Revisionszähler; ein Konflikt (409) liefert err.data.rev
    async mapList(all?: boolean): Promise<MapMeta[]> { return (await (await call('GET', '/api/xf/maps' + (all ? '?all=1' : ''))).json()).items; },
    async mapGet(id: string): Promise<MapFull> { return (await call('GET', '/api/xf/maps/' + encodeURIComponent(id))).json() as Promise<MapFull>; },
    async mapCreate(name: string, text: string): Promise<MapMeta> { return jsonCall('POST', '/api/xf/maps', { name: name, text: text }); },
    async mapSave(id: string, baseRev: number, change: { text?: string; name?: string }): Promise<MapMeta> { return jsonCall('PUT', '/api/xf/maps/' + encodeURIComponent(id), Object.assign({ baseRev: baseRev }, change)); },
    // ---- Freigaben (nur Besitzer und Admins): per E-Mail an andere Benutzer, lesend oder schreibend
    async shareList(id: string): Promise<{ items: ShareInfo[]; max: number }> { return (await call('GET', '/api/xf/maps/' + encodeURIComponent(id) + '/shares')).json() as Promise<{ items: ShareInfo[]; max: number }>; },
    async shareSet(id: string, email: string, perm: 'read' | 'write'): Promise<ShareInfo> { return jsonCall('PUT', '/api/xf/maps/' + encodeURIComponent(id) + '/shares', { email: email, perm: perm }); },
    /** Freigabe entziehen; mit der eigenen Benutzer-ID verlässt man eine mit einem geteilte Karte */
    async shareRemove(id: string, userId: string): Promise<void> { await call('DELETE', '/api/xf/maps/' + encodeURIComponent(id) + '/shares/' + encodeURIComponent(userId)); },
    async mapRemove(id: string): Promise<void> { await call('DELETE', '/api/xf/maps/' + encodeURIComponent(id)); },
    async mapRevisions(id: string): Promise<RevisionList> { return (await call('GET', '/api/xf/maps/' + encodeURIComponent(id) + '/revisions')).json() as Promise<RevisionList>; },
    async mapRevision(id: string, rev: number): Promise<{ rev: number; size: number; text: string }> { return (await call('GET', '/api/xf/maps/' + encodeURIComponent(id) + '/revisions/' + Number(rev))).json() as Promise<{ rev: number; size: number; text: string }>; },
    // ---- Benutzereinstellungen (user_settings, Standard-API von PocketBase): null, wenn noch keine gespeichert sind
    async settingsGet(): Promise<{ id: string; data: unknown } | null> {
      const j = await (await call('GET', '/api/collections/user_settings/records?perPage=1')).json();
      const r = j.items && j.items[0];
      return r ? { id: r.id, data: r.data } : null;
    },
    async settingsSave(id: string | null, userId: string, data: Snapshot): Promise<{ id?: string }> {
      return id ? jsonCall('PATCH', '/api/collections/user_settings/records/' + encodeURIComponent(id), { data: data })
        : jsonCall('POST', '/api/collections/user_settings/records', { user: userId, data: data });
    },
    // ---- Compile (Phase 5): Jobs starten, beobachten, abbrechen; Ergebnisdateien holen
    /** threads: nur für Admins (der Server lehnt es bei anderen mit 403 ab); ohne Angabe gilt die Vorgabe des Workers */
    async compileStart(mapId: string, profile: string, engine?: string, threads?: number): Promise<CompileJob> {
      const body: Record<string, unknown> = { mapId: mapId, profile: profile, engine: engine || 'sdhlt' };
      if (threads) body.threads = threads;
      return jsonCall('POST', '/api/xf/compile', body);
    },
    async compileList(all?: boolean): Promise<CompileJob[]> { return (await (await call('GET', '/api/xf/compile' + (all ? '?all=1' : ''))).json()).items; },
    async compileQueue(): Promise<QueueInfo> { return (await call('GET', '/api/xf/compile/queue')).json() as Promise<QueueInfo>; },
    async compileGet(id: string, withLog?: boolean): Promise<{ job: CompileJob; log?: string }> { return (await call('GET', '/api/xf/compile/' + encodeURIComponent(id) + (withLog ? '?log=1' : ''))).json() as Promise<{ job: CompileJob; log?: string }>; },
    async compileCancel(id: string): Promise<CompileJob> { return (await call('POST', '/api/xf/compile/' + encodeURIComponent(id) + '/cancel')).json() as Promise<CompileJob>; },
    async compileRemove(id: string): Promise<void> { await call('DELETE', '/api/xf/compile/' + encodeURIComponent(id)); },
    async compileFile(id: string, name: string): Promise<ArrayBuffer> { return (await call('GET', '/api/xf/compile/' + encodeURIComponent(id) + '/files/' + encodeURIComponent(name))).arrayBuffer(); },
    async fetchBuffer(path: string): Promise<ArrayBuffer> { return (await call('GET', '/assets/' + encPath(path))).arrayBuffer(); }
  };
}
export type BackendClient = ReturnType<typeof create>;

// ---- gemerkte Einstellungen (nur im Browser, geschützt)
interface Store { getItem(k: string): string | null; setItem(k: string, v: string): void }
function loadSettings(storage: Store): StoredSettings {
  try {
    const s = JSON.parse(storage.getItem(STORE) as string);
    if (s && typeof s === 'object') {
      const u: BackendUser | null = s.user && typeof s.user === 'object' && s.user.id ? { id: String(s.user.id), email: String(s.user.email || ''), role: s.user.role === 'admin' ? 'admin' : 'user' } : null;
      const token = String(s.token || '');
      return { base: normBase(s.base), token: token, email: String(s.email || ''), user: token ? u : null };
    }
  } catch (e) { /* leer */ }
  return { base: '', token: '', email: '', user: null };
}
function saveSettings(storage: { setItem(k: string, v: string): void }, s: Partial<StoredSettings>): void {
  try { storage.setItem(STORE, JSON.stringify({ base: normBase(s.base), token: s.token || '', email: s.email || '', user: s.token && s.user ? s.user : null })); } catch (e) { /* kein Speicher */ }
}

const Backend = { create, normBase, targetPath, wadNames, loadSettings, saveSettings, STORE };
export default Backend;
